import { useEffect, useState } from 'react';
import { LifeBuoy, Inbox, UserPlus } from 'lucide-react';
import Modal from '../ui/Modal';
import Ayuda from '../ui/Ayuda';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { leerModuloUrl } from '../lib/urlEstado';
import { impactoUrgencia } from './gravedad';
import FormularioReporte from './FormularioReporte';
import SugerenciasArticulos from './SugerenciasArticulos';

/* =====================================================================
   Reportar un problema (con cuenta) · DIRECTRIZ-TICKETS F2
   =====================================================================
   El modal que abre el botón «Reportar un problema» de cualquier pantalla.
   Pide solo qué pasa y qué tan grave es; prellena el contexto técnico
   —pantalla, versión, dispositivo— SIN datos sensibles, para que quien
   atiende sepa desde dónde se reportó sin que nadie lo escriba.
   ===================================================================== */

// Un nombre corto y legible del aparato, para la bitácora. No es una
// huella: es «Android», «iPhone», «Computadora» — lo justo para que quien
// atiende sepa si el problema es del teléfono o del escritorio.
function dispositivoLegible() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iphone|ipod/i.test(ua)) return 'iPhone';
  if (/ipad/i.test(ua)) return 'iPad';
  if (/android/i.test(ua)) return 'Android';
  if (/windows|macintosh|linux/i.test(ua)) return 'Computadora';
  return ua ? 'Otro dispositivo' : '';
}

// La versión sellada en el paquete al construirlo (vite.config.js,
// `__TEGO_VERSION__`); fuera de un paquete sellado, vacío.
function versionDeEstaPagina() {
  return typeof __TEGO_VERSION__ !== 'undefined' ? String(__TEGO_VERSION__) : '';
}

export default function Reportar({ onCerrar, onEnviado, onIr }) {
  const { tieneNivel } = useAuth();
  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState('media');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [folio, setFolio] = useState('');
  const [version, setVersion] = useState('');
  // A QUIÉN LLEGA EL REPORTE. Un botón que manda al vacío es peor que
  // ninguno: aquí se dice a quién llega, y si NO hay nadie con permiso de
  // atender, a quien administra se le ofrece nombrar un encargado. Es la
  // mesa de ayuda INTERNA de la empresa, no soporte de TEGO.
  const [destino, setDestino] = useState(null);

  // La versión, de mejor esfuerzo: si `/nodo` no contesta, se omite. El
  // backend recorta el contexto a lo que llegue, así que nunca estorba.
  //
  // `/nodo` ES DE ADMINISTRADOR (routers/nodo.py, `requiere_admin`). A
  // quien no lo es no se le pide: la operadora provocaba un 403 cada vez
  // que abría «Reportar un problema», y el navegador lo pinta como error
  // en la consola (LISTO-PARA-PILOTO b, P8). A ella le basta la versión
  // con la que se construyó esta página, que es la que está viendo.
  const puedeLeerNodo = tieneNivel('ADMIN');
  useEffect(() => {
    let vivo = true;
    if (puedeLeerNodo) {
      api.get('/nodo').then((d) => { if (vivo) setVersion(d?.version || ''); })
        .catch(() => {});
    } else {
      setVersion(versionDeEstaPagina());
    }
    api.get('/tickets/destino').then((d) => { if (vivo) setDestino(d); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [puedeLeerNodo]);

  async function enviar() {
    if (!descripcion.trim() || enviando) return;
    setEnviando(true);
    setError('');
    try {
      const { impacto, urgencia } = impactoUrgencia(gravedad);
      const contexto = {
        pantalla: leerModuloUrl() || (typeof document !== 'undefined' ? document.title : ''),
        version,
        dispositivo: dispositivoLegible(),
      };
      const t = await api.post('/tickets', {
        titulo: descripcion.trim().split('\n')[0].slice(0, 160),
        descripcion: descripcion.trim(),
        impacto,
        urgencia,
        contexto,
      });
      setFolio(t.folio);
      if (onEnviado) onEnviado(t);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo enviar el reporte. Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Pedir ayuda a TI"
      subtitulo="Cuéntanos qué pasa y qué tan grave es. Lo demás (pantalla, versión y equipo) se anota por ti."
      icono={LifeBuoy}
      onCerrar={onCerrar}
      bloqueado={enviando}
      guardia={!folio}
    >
      <div className="p-4">
        <Ayuda id="reportar.pantalla" />
        {/* A QUIÉN LLEGA: el reporte es para la mesa de ayuda de tu empresa,
            no para TEGO. Si nadie tiene permiso de atender, a quien administra
            se le ofrece nombrar un encargado; a los demás se les dice que llega
            al administrador, para que nadie reporte al vacío. */}
        {!folio && destino && (
          !destino.hay_quien_atiende && tieneNivel('ADMIN') ? (
            <div role="status" className="mb-3 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
              <Inbox className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">Nadie recibe estos reportes todavía</p>
                <p className="mt-0.5 text-sm text-amber-800">Nombra a alguien con permiso para atender la mesa de ayuda; mientras tanto, los reportes te llegan a ti.</p>
                {onIr && (
                  <button
                    type="button"
                    onClick={() => { onIr('usuarios'); onCerrar(); }}
                    className="mt-2 inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white hover:bg-amber-700"
                  >
                    <UserPlus className="h-4 w-4" aria-hidden="true" /> Asignar encargado
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="mb-3 rounded-xl bg-gray-100 px-3 py-2 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">Lo recibe:</span> {destino.a_quien_llega}.
            </p>
          )
        )}
        {/* Antes de abrir el ticket, TEGO sugiere soluciones parecidas de la
            empresa. Si alguna sirve, la persona se ahorra el reporte (F7). */}
        {!folio && <SugerenciasArticulos texto={descripcion} />}
        <div className="mt-3">
          <FormularioReporte
            descripcion={descripcion}
            onDescripcion={setDescripcion}
            gravedad={gravedad}
            onGravedad={setGravedad}
            enviando={enviando}
            error={error}
            folio={folio}
            onEnviar={enviar}
          />
          {folio && (
            <button
              type="button"
              onClick={onCerrar}
              className="mt-4 min-h-[48px] w-full rounded-xl border border-gray-300
                         font-medium text-gray-700 hover:bg-gray-50"
            >
              Listo
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
